// Shared default config schemas for each module type. Used by ModuleLibrary
// (content-only defaults) and ModuleInstances (per-instance configs with
// style/colour overrides).
//
// IMPORTANT: Visual-style fields (colours, font family, weight, sizes,
// borders, etc.) default to empty strings so the public renderer falls
// back to Site Styles CSS variables (`var(--site-*)`). This way, every
// module instance "follows the site styles" out of the box and only
// becomes opinionated when an admin explicitly overrides a value.

import { DEFAULT_HEADING_LEVEL } from "@/utils/headingSize";

export const MODULE_TYPES = [
  { value: "header", label: "Header", description: "Site header with logo and branding" },
  { value: "navigation", label: "Navigation", description: "Site navigation with menu items and CTA" },
  { value: "footer", label: "Footer", description: "Footer with links and copyright" },
  { value: "text", label: "Text/HTML Content", description: "Rich text or HTML content block" },
  { value: "hero", label: "Hero Banner", description: "Large banner with CTA" },
  { value: "all_properties", label: "All Properties", description: "Display all enabled properties" },
  { value: "featured_properties", label: "Featured Properties", description: "Display featured properties" },
  { value: "property_gallery", label: "Property Image Gallery", description: "Manage and display the image gallery of a specific property" },
  { value: "property_article", label: "Property Article", description: "AI-generated blog articles grounded in your latest listings" },
  { value: "card", label: "Card", description: "25%-image / 75%-content card (manual fields OR auto-listed properties via URL filters)" },
  { value: "go_to", label: "Go To", description: "Up to four CTAs to navigate between pages" },
  { value: "products", label: "Products / Shop", description: "Grid of live products from the catalogue (with Add to basket)" },
  { value: "basket", label: "Basket", description: "Inline basket contents with remove + Checkout CTA" },
  { value: "checkout", label: "Checkout", description: "Customer details form + Stripe payment redirect", usesCookies: true, cookieNote: "Relies on the shopper sign-in session cookie to attach the order to a signed-in shopper's account." },
  { value: "verify_photo", label: "Verify Photo", description: "Upload two photos and get an AI-driven likeness score", usesCookies: true, cookieNote: "Relies on the shopper sign-in session cookie to attribute likeness-check usage to a signed-in shopper." },
  { value: "milestones", label: "Property Milestones", description: "Agent-only dashboard to track per-property milestones and progress" },
  { value: "capture_property_form", label: "Capture Property Form", description: "Public form for unregistered owners to submit a property — captured as an unclaimed lead", usesCookies: true, cookieNote: "Saves the address fields the visitor types into a \"capturedpropertydetails\" cookie (30 days), so the House Prices tool can pre-fill their postcode later." },
  { value: "house_prices_tool", label: "House Prices", description: "Public postcode → AI price estimate tool (same as the agent dashboard's House Prices section) + a CTA to any page", usesCookies: true, cookieNote: "Reads/writes the same \"capturedpropertydetails\" cookie as the capture form, and relies on the shopper sign-in session cookie to gate + email results." },
  { value: "planning_applications_tool", label: "Planning Applications", description: "Public postcode search of live UK planning applications (PlanIt) + a CTA to any page" },
  { value: "planning_appeals_tool", label: "Planning Appeals", description: "Public postcode/street search of UK Planning Inspectorate appeal decisions + a CTA to any page" },
  { value: "pricing_table", label: "Pricing Table", description: "Manageable multi-tier price comparison (name, price, features, CTA per tier)" },
  { value: "cards_grid", label: "Cards Grid", description: "Add up to 16 cards (4 per row, 4 rows) — each with a heading, sub-heading, rich text, CTA, background image and its own border style" },
  { value: "advert", label: "Advert", description: "Portrait social-ad graphic — background image (zoom/transparency/blur), a top-left logo badge, and up to 4 rich-text cards positioned above, below or overlapping the image" },
];

// Module types that have configurable fields (others are auto-rendered).
export const CONFIGURABLE_TYPES = [
  "hero",
  "text",
  "navigation",
  "header",
  "footer",
  "go_to",
  "all_properties",
  "featured_properties",
  "property_gallery",
  "property_article",
  "card",
  "products",
  "basket",
  "checkout",
  "verify_photo",
  "milestones",
  "capture_property_form",
  "house_prices_tool",
  "planning_applications_tool",
  "planning_appeals_tool",
  "pricing_table",
  "cards_grid",
  "advert",
];
// Shared defaults for the configurable heading + CTA block used by the
// properties listing modules.
function headingCtaDefaults(existing, fallbackHeading, fallbackSubheading, fallbackCtaText, fallbackCtaLink) {
  return {
    // Preserve every existing key. Without this, any config field the admin
    // set that isn't enumerated below (e.g. alignment, weight, per-instance
    // spacing, section background) is silently stripped on the next
    // autosave and their edit "reverts" on the public site.
    ...existing,
    // Content
    heading_text: existing.heading_text || fallbackHeading,
    subheading_text: existing.subheading_text || fallbackSubheading,
    cta_enabled: existing.cta_enabled !== undefined ? existing.cta_enabled : false,
    cta_text: existing.cta_text || fallbackCtaText,
    cta_link: existing.cta_link !== undefined ? existing.cta_link : fallbackCtaLink,
    cta_align: existing.cta_align || "center",
    cta_position: existing.cta_position || "top",
    heading_align: existing.heading_align || "center",
    // Style (default empty → Site Styles)
    heading_font_family: existing.heading_font_family || "",
    heading_font_size: existing.heading_font_size || DEFAULT_HEADING_LEVEL,
    heading_font_weight: existing.heading_font_weight || "",
    heading_font_style: existing.heading_font_style || "",
    heading_text_transform: existing.heading_text_transform || "",
    heading_color: existing.heading_color || "",
    subheading_color: existing.subheading_color || "",
    subheading_font_size: existing.subheading_font_size || DEFAULT_HEADING_LEVEL,
    cta_bg_color: existing.cta_bg_color || "",
    cta_text_color: existing.cta_text_color || "",
    cta_border_radius: existing.cta_border_radius ?? "",
  };
}

export function getDefaultConfig(type, existing = {}) {
  switch (type) {
    case "hero":
      return {
        // Preserve every existing key — see comment in `products` case for
        // the "fields silently stripped on autosave" bug this guards.
        ...existing,
        // Content
        title: existing.title || "",
        subtitle: existing.subtitle || "",
        background_image: existing.background_image || "",
        cta_text: existing.cta_text || "",
        cta_link: existing.cta_link || "",
        // Style (default empty → Site Styles)
        title_color: existing.title_color || "",
        subtitle_color: existing.subtitle_color || "",
        cta_font_family: existing.cta_font_family || "",
        cta_font_size: existing.cta_font_size || DEFAULT_HEADING_LEVEL,
        cta_font_weight: existing.cta_font_weight || "",
        cta_font_style: existing.cta_font_style || "",
        cta_text_transform: existing.cta_text_transform || "",
        cta_letter_spacing: existing.cta_letter_spacing ?? "",
        cta_bg_color: existing.cta_bg_color || "",
        cta_text_color: existing.cta_text_color || "",
        cta_hover_bg_color: existing.cta_hover_bg_color || "",
        cta_hover_text_color: existing.cta_hover_text_color || "",
        cta_border_color: existing.cta_border_color || "",
        cta_border_width: existing.cta_border_width ?? "",
        cta_border_radius: existing.cta_border_radius ?? "",
        banner_height: existing.banner_height ?? "",
        image_zoom: existing.image_zoom ?? "",
        image_rotate: existing.image_rotate ?? "",
        image_opacity: existing.image_opacity ?? "",
        image_contrast: existing.image_contrast ?? "",
        image_brightness: existing.image_brightness ?? "",
      };
    case "text":
      return {
        ...existing,
        content: existing.content || "",
        // Optional AI-generation prompt. Empty by default; the admin edits
        // it in the text module config panel and clicks "AI" to regenerate
        // the `content` field.
        ai_prompt: existing.ai_prompt || "",
      };
    case "header":
      return {
        ...existing,
        // Content
        logo_url: existing.logo_url || "",
        logo_text: existing.logo_text || "Sell Local Homes",
        strap_line: existing.strap_line || "",
        cta_text: existing.cta_text || "Request Viewing",
        cta_link: existing.cta_link || "/contact",
        show_login: existing.show_login !== undefined ? existing.show_login : false,
        login_link: existing.login_link || "/admin/login",
        // Style (default empty → Site Styles)
        logo_font_size: existing.logo_font_size || DEFAULT_HEADING_LEVEL,
        strap_line_font_size: existing.strap_line_font_size || DEFAULT_HEADING_LEVEL,
        strap_line_color: existing.strap_line_color || "",
        cta_bg_color: existing.cta_bg_color || "",
        cta_text_color: existing.cta_text_color || "",
      };
    case "navigation":
      return {
        ...existing,
        // Content / behaviour
        cta_enabled: existing.cta_enabled !== false,
        cta_text: existing.cta_text || "Request Viewing",
        cta_link: existing.cta_link || "/contact",
        show_login: existing.show_login || false,
        enable_dropdown: existing.enable_dropdown !== false,
        // "both" (icon + text — default) | "text" (text only) | "icon" (icon only)
        display_mode: existing.display_mode || "both",
        // Style (default empty → Site Styles)
        bg_color: existing.bg_color || "",
        text_color: existing.text_color || "",
        hover_color: existing.hover_color || "",
        font_size: existing.font_size || DEFAULT_HEADING_LEVEL,
        cta_bg_color: existing.cta_bg_color || "",
        cta_text_color: existing.cta_text_color || "",
      };
    case "footer":
      return {
        ...existing,
        // Content only — footer style is configured per-instance.
        company_name: existing.company_name || "SellLocalHomes",
        tagline: existing.tagline || "Your trusted partner in selling your home.",
        copyright_year: existing.copyright_year || new Date().getFullYear(),
      };
    case "go_to":
      return {
        ...existing,
        // Layout / content
        cta_size: existing.cta_size || "md",
        cta_placement: existing.cta_placement || "horizontal",
        // Style variant — decides how each CTA renders on the public page.
        // "card" (default) keeps the legacy tall-card layout; the button
        // variants match the Hero primary button so the CTAs look
        // consistent with the rest of the site.
        cta_style: existing.cta_style || "card",
        cta_bg_color: existing.cta_bg_color || "",
        cta_text_color: existing.cta_text_color || "",
        cta_border_color: existing.cta_border_color || "",
        cta_border_width: existing.cta_border_width ?? "",
        cta_border_radius: existing.cta_border_radius ?? "",
        cta1_title: existing.cta1_title || "",
        cta1_description: existing.cta1_description || "",
        cta1_page_slug: existing.cta1_page_slug !== undefined ? existing.cta1_page_slug : "",
        cta1_icon: existing.cta1_icon || "",
        cta2_title: existing.cta2_title || "",
        cta2_description: existing.cta2_description || "",
        cta2_page_slug: existing.cta2_page_slug !== undefined ? existing.cta2_page_slug : "",
        cta2_icon: existing.cta2_icon || "",
        cta3_title: existing.cta3_title || "",
        cta3_description: existing.cta3_description || "",
        cta3_page_slug: existing.cta3_page_slug !== undefined ? existing.cta3_page_slug : "",
        cta3_icon: existing.cta3_icon || "",
        cta4_title: existing.cta4_title || "",
        cta4_description: existing.cta4_description || "",
        cta4_page_slug: existing.cta4_page_slug !== undefined ? existing.cta4_page_slug : "",
        cta4_icon: existing.cta4_icon || "",
        // Style (default empty → Site Styles)
        font_size: existing.font_size || DEFAULT_HEADING_LEVEL,
      };
    case "property_gallery":
      return {
        ...existing,
        // Content: bind to one property OR fall back to the current page
        // property when rendered inside a per-property layout in future.
        property_id: existing.property_id || "",
        heading_text: existing.heading_text !== undefined ? existing.heading_text : "",
        // Layout
        columns: existing.columns || 3,        // 1, 2, 3, 4
        aspect_ratio: existing.aspect_ratio || "4:3", // 1:1, 4:3, 16:9, 3:2
        gap: existing.gap || "md",             // sm, md, lg
        rounded: existing.rounded !== undefined ? existing.rounded : true,
        show_featured_label: existing.show_featured_label !== undefined ? existing.show_featured_label : false,
        max_images: existing.max_images ?? "", // blank → show all
        lightbox: existing.lightbox !== undefined ? existing.lightbox : true,
        // Style (default empty → Site Styles)
        heading_font_size: existing.heading_font_size || DEFAULT_HEADING_LEVEL,
        heading_color: existing.heading_color || "",
      };
    case "all_properties":
      return headingCtaDefaults(
        existing,
        "All Properties",
        "Browse all our available properties",
        "View All Properties",
        "/properties"
      );
    case "featured_properties":
      return headingCtaDefaults(
        existing,
        "Featured Properties",
        "Discover our handpicked featured listings",
        "View Featured",
        "/properties"
      );
    case "card":
      return {
        ...existing,
        // Data source: "manual" or "properties_from_url"
        data_source: existing.data_source || "manual",
        // --- Manual mode fields ---
        image: existing.image || "",
        heading: existing.heading !== undefined ? existing.heading : "Card Heading",
        description: existing.description !== undefined ? existing.description : "Add a description that summarises this card.",
        type_text: existing.type_text !== undefined ? existing.type_text : "",
        location_text: existing.location_text !== undefined ? existing.location_text : "",
        price_text: existing.price_text !== undefined ? existing.price_text : "",
        cta_text: existing.cta_text !== undefined ? existing.cta_text : "More",
        cta_link: existing.cta_link !== undefined ? existing.cta_link : "/properties",
        // --- Property-list (URL-filter) mode fields ---
        // Which URL query keys are honored when filtering properties.
        // Each maps to a corresponding /api/properties query param.
        filter_location: existing.filter_location !== undefined ? existing.filter_location : true,
        filter_property_type: existing.filter_property_type !== undefined ? existing.filter_property_type : true,
        filter_min_price: existing.filter_min_price !== undefined ? existing.filter_min_price : true,
        filter_max_price: existing.filter_max_price !== undefined ? existing.filter_max_price : true,
        filter_beds: existing.filter_beds !== undefined ? existing.filter_beds : true,
        filter_baths: existing.filter_baths !== undefined ? existing.filter_baths : false,
        filter_featured: existing.filter_featured !== undefined ? existing.filter_featured : false,
        max_results: existing.max_results ?? 12,
        empty_message: existing.empty_message !== undefined ? existing.empty_message : "No properties match the current filters.",
        // --- Layout ---
        image_aspect_ratio: existing.image_aspect_ratio || "4:3", // 1:1, 4:3, 16:9, 3:2
        image_rounded: existing.image_rounded !== undefined ? existing.image_rounded : true,
        // --- Style (default empty → Site Styles) ---
        heading_font_size: existing.heading_font_size || DEFAULT_HEADING_LEVEL,
        heading_color: existing.heading_color || "",
      };
    case "products":
      // Preserve every existing key so recently-added config knobs
      // (layout, image_scale_pct, module_title_*, subtitle_*,
      // section_title_*, alignments, …) don't get silently stripped
      // when the admin re-opens the instance and autosave fires.
      return {
        ...existing,
        title: existing.title !== undefined ? existing.title : "Shop",
        subtitle: existing.subtitle || "",
        columns: existing.columns || 3,
        card_width: existing.card_width ?? 0,   // 0 = use columns; else auto-fill min px
        limit: existing.limit ?? 0,
        default_category: existing.default_category || "",
        show_category_line: existing.show_category_line !== false,  // default on
        layout: existing.layout || "table",
      };
    case "basket":
      return {
        ...existing,
        title: existing.title !== undefined ? existing.title : "Your Basket",
        subtitle: existing.subtitle || "",
        checkout_url: existing.checkout_url || "/checkout",
        continue_url: existing.continue_url || "/",
        continue_label: existing.continue_label || "Continue shopping",
        hide_when_empty: existing.hide_when_empty === true,
      };
    case "checkout":
      return {
        ...existing,
        title: existing.title !== undefined ? existing.title : "Checkout",
        subtitle: existing.subtitle !== undefined
          ? existing.subtitle
          : "You'll be redirected to Stripe's secure checkout to complete payment.",
        empty_url: existing.empty_url || "/",
        empty_label: existing.empty_label || "Continue shopping",
      };
    case "milestones":
      return {
        ...existing,
        heading: existing.heading !== undefined ? existing.heading : "My Property Dashboard",
        subheading: existing.subheading !== undefined
          ? existing.subheading
          : "Track milestones and progress for each of your properties.",
        default_milestones: Array.isArray(existing.default_milestones) && existing.default_milestones.length
          ? existing.default_milestones
          : [
              "Upload listing photos",
              "Upload listing floorplan",
              "Complete listing details",
              "Obtain EPC",
              "Obtain Electrical Safety Certificate",
              "Obtain Gas Safety Certificate",
              "Accept an offer",
              "Instruct a solicitor/conveyancer",
              "Buyer's mortgage survey & valuation completed",
              "Buyer's Searches & enquiries completed",
              "Buyer's Mortgage offer issued completed",
              "Exchange of contracts",
              "Set Completion date",
              "Buyer to Transfer Deposit",
              "Exchange complete",
              "Completion",
              "Hand over keys",
            ],
      };
    case "capture_property_form":
      return {
        ...existing,
        heading: existing.heading !== undefined ? existing.heading : "List Your Property",
        subheading: existing.subheading !== undefined
          ? existing.subheading
          : "Tell us about your property — we'll email you a link to finish setting up your listing.",
        submit_button_text: existing.submit_button_text || "Submit Property",
        success_heading: existing.success_heading || "Thanks — check your inbox",
        success_message: existing.success_message !== undefined
          ? existing.success_message
          : "We've emailed you a link to set a password and finish publishing your listing. If you submitted more than one property, one link unlocks all of them.",
      };
    case "house_prices_tool":
      return {
        ...existing,
        heading: existing.heading !== undefined ? existing.heading : "What's Your Home Worth?",
        subheading: existing.subheading !== undefined
          ? existing.subheading
          : "Get an instant AI price estimate grounded in real sold prices for your postcode.",
        cta_text: existing.cta_text || "",
        cta_page_slug: existing.cta_page_slug !== undefined ? existing.cta_page_slug : "",
      };
    case "planning_applications_tool":
      return {
        ...existing,
        heading: existing.heading !== undefined ? existing.heading : "Planning Applications Near You",
        subheading: existing.subheading !== undefined
          ? existing.subheading
          : "Search live UK planning applications by postcode.",
        cta_text: existing.cta_text || "",
        cta_page_slug: existing.cta_page_slug !== undefined ? existing.cta_page_slug : "",
      };
    case "planning_appeals_tool":
      return {
        ...existing,
        heading: existing.heading !== undefined ? existing.heading : "Planning Appeal Decisions",
        subheading: existing.subheading !== undefined
          ? existing.subheading
          : "Search UK Planning Inspectorate appeal decisions by postcode or street name.",
        cta_text: existing.cta_text || "",
        cta_page_slug: existing.cta_page_slug !== undefined ? existing.cta_page_slug : "",
      };
    case "pricing_table":
      return {
        ...existing,
        heading: existing.heading !== undefined ? existing.heading : "Choose Your Plan",
        subheading: existing.subheading !== undefined ? existing.subheading : "Pick the plan that's right for you",
        heading_color: existing.heading_color || "",
        card_bg_color: existing.card_bg_color || "",
        highlight_bg_color: existing.highlight_bg_color || "",
        highlight_accent_color: existing.highlight_accent_color || "",
        cta_bg_color: existing.cta_bg_color || "",
        tiers: Array.isArray(existing.tiers) && existing.tiers.length ? existing.tiers : [
          {
            name: "Basic", price: "£9", interval: "/mo",
            description: "Everything you need to get started.",
            features: ["1 property listing", "Basic support", "Standard visibility"],
            highlighted: false, badge_text: "", cta_text: "Choose Basic", cta_link: "/contact",
          },
          {
            name: "Standard", price: "£19", interval: "/mo",
            description: "Our most popular plan for active sellers.",
            features: ["Up to 5 property listings", "Priority support", "Featured visibility", "Social media promotion"],
            highlighted: true, badge_text: "Most Popular", cta_text: "Choose Standard", cta_link: "/contact",
          },
          {
            name: "Premium", price: "£39", interval: "/mo",
            description: "Full-service support for serious sellers.",
            features: ["Unlimited property listings", "Dedicated account manager", "Premium placement", "Full marketing suite"],
            highlighted: false, badge_text: "", cta_text: "Choose Premium", cta_link: "/contact",
          },
        ],
      };
    case "cards_grid":
      return {
        ...existing,
        heading: existing.heading !== undefined ? existing.heading : "",
        subheading: existing.subheading !== undefined ? existing.subheading : "",
        heading_color: existing.heading_color || "",
        subheading_color: existing.subheading_color || "",
        // Up to 16 cards (4 per row × 4 rows). Each row auto-sizes to its
        // tallest card; columns are always equal width/gap.
        cards: Array.isArray(existing.cards) ? existing.cards : [],
      };
    case "advert":
      return {
        ...existing,
        container_bg_color: existing.container_bg_color || "#102a3d",
        container_max_width: existing.container_max_width || 420,
        container_padding: existing.container_padding ?? 20,
        container_radius: existing.container_radius ?? 16,
        background_image: existing.background_image || "",
        image_height: existing.image_height || 320,
        image_zoom: existing.image_zoom || 100,
        image_opacity: existing.image_opacity ?? 100,
        image_blur: existing.image_blur || 0,
        logo_enabled: existing.logo_enabled !== false,
        logo_text: existing.logo_text || "",
        cards: Array.isArray(existing.cards) ? existing.cards : [],
      };
    default:
      return { ...existing };
  }
}

export function getModuleTypeLabel(type) {
  return MODULE_TYPES.find((m) => m.value === type)?.label || type;
}

// ---------------------------------------------------------------------------
// Style key registry — used by the "Reset to Site Styles" action on every
// module instance. Listing keys explicitly (rather than reflecting off a
// regex) keeps the reset deterministic and prevents accidental wipes of
// content fields that happen to share a prefix.
// ---------------------------------------------------------------------------
const TEXT_STYLE_SUFFIXES = [
  "font_family",
  "font_weight",
  "font_style",
  "font_size",
  "text_transform",
  "letter_spacing",
  "color",
  "text_align",
];

function withSuffixes(prefixes) {
  const out = [];
  prefixes.forEach((p) => {
    TEXT_STYLE_SUFFIXES.forEach((s) => out.push(`${p}${s}`));
  });
  return out;
}

export const STYLE_KEYS_BY_TYPE = {
  hero: [
    // Title / subtitle plain-text style controls
    ...withSuffixes(["title_", "subtitle_"]),
    "title_color",
    "subtitle_color",
    // Per-element justification (title/subtitle use *_text_align via withSuffixes)
    "cta_align",
    // CTA button style
    "cta_font_family",
    "cta_font_weight",
    "cta_font_style",
    "cta_font_size",
    "cta_text_transform",
    "cta_letter_spacing",
    "cta_bg_color",
    "cta_text_color",
    "cta_hover_bg_color",
    "cta_hover_text_color",
    "cta_border_color",
    "cta_border_width",
    "cta_border_radius",
    // Banner & image filters
    "banner_height",
    "image_zoom",
    "image_rotate",
    "image_opacity",
    "image_contrast",
    "image_brightness",
  ],
  header: [
    ...withSuffixes(["logo_", "strap_line_", "cta_"]),
    "logo_font_size",
    "strap_line_font_size",
    "strap_line_color",
    "cta_bg_color",
    "cta_text_color",
  ],
  navigation: [
    ...withSuffixes(["strap_line_", "cta_", "link_"]),
    "bg_color",
    "text_color",
    "hover_color",
    "font_size",
    "strap_line_font_size",
    "strap_line_color",
    "cta_bg_color",
    "cta_text_color",
  ],
  footer: [
    ...withSuffixes(["company_name_", "tagline_", "link_", "copy_"]),
    "bg_color",
    "text_color",
    "link_color",
    "link_hover_color",
    "link_font_size",
    "copy_font_size",
  ],
  go_to: [
    ...withSuffixes(["title_", "description_", "cta_"]),
    "font_size",
    "card_bg_color",
    "card_text_color",
    "card_border_color",
  ],
  all_properties: [
    ...withSuffixes(["heading_", "subheading_", "cta_"]),
    "heading_color",
    "subheading_color",
    "cta_bg_color",
    "cta_text_color",
    "cta_border_radius",
  ],
  featured_properties: [
    ...withSuffixes(["heading_", "subheading_", "cta_"]),
    "heading_color",
    "subheading_color",
    "cta_bg_color",
    "cta_text_color",
    "cta_border_radius",
  ],
  text: [],
  property_gallery: [
    ...withSuffixes(["heading_"]),
    "heading_color",
    "heading_font_size",
  ],
  card: [
    ...withSuffixes(["heading_"]),
    "heading_color",
    "heading_font_size",
  ],
  cards_grid: [
    ...withSuffixes(["heading_", "subheading_"]),
    "heading_color",
    "subheading_color",
  ],
};

/**
 * Returns a config object with every style/colour key for the given type
 * cleared to an empty string. Used by the "Reset to Site Styles" action.
 * Content keys (titles, links, image URLs, text, toggles) are preserved.
 */
export function resetStyleConfig(type, config = {}) {
  const styleKeys = STYLE_KEYS_BY_TYPE[type] || [];
  const out = { ...config };
  // De-dupe — `withSuffixes` plus explicit additions can repeat.
  const seen = new Set();
  styleKeys.forEach((k) => {
    if (seen.has(k)) return;
    seen.add(k);
    if (k in out) out[k] = "";
  });
  return out;
}
